import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { CookingPot, ShareNetwork } from '@phosphor-icons/react';

import { CookQRModal } from '@/components/meals/Shared';
import PlannerTab from '@/components/meals/PlannerTab';
import WhatsInKitchenTab from '@/components/meals/WhatsInKitchenTab';
import FoodProfileTab from '@/components/meals/FoodProfileTab';
import EaterProfilesTab from '@/components/meals/EaterProfilesTab';
import RecipesTab from '@/components/meals/RecipesTab';
import RatingsTab from '@/components/meals/RatingsTab';
import MealsHowToTour from '@/components/meals/MealsHowToTour';
import AIChefTab from '@/components/meals/AIChefTab';
import DabbaTab from '@/components/meals/DabbaTab';

// Re-exports for any external consumer that imports helpers from this page.
export { MealSlotDisplay, flattenSlotForRating, slotToChips } from '@/components/meals/utils';

const TABS = [
  { id: 'planner', label: 'This Week' },
  { id: 'kitchen', label: "What's in Kitchen?" },
  { id: 'profile', label: 'Food Profile' },
  { id: 'eaters', label: 'Eaters' },
  { id: 'recipes', label: 'Recipes' },
  { id: 'ratings', label: 'Ratings' },
  { id: 'ai-chef', label: 'AI Chef' },
  { id: 'dabba', label: 'Kids Dabba' },
];

export default function MealPlannerPage() {
  const { api } = useAuth();
  const [tab, setTab] = useState('planner');
  const [plans, setPlans] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCookQR, setShowCookQR] = useState(false);
  // pendingAction is dispatched by BriefCard → consumed once by AI Chef tab
  const [pendingAction, setPendingAction] = useState(null);

  const triggerAction = useCallback((item) => {
    setPendingAction(item);
    setTab('ai-chef');
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [p, r, pr] = await Promise.all([
        api.get('/meals/plans'),
        api.get('/meals/recipes'),
        api.get('/meals/preferences'),
      ]);
      setPlans(p.data);
      setRecipes(r.data);
      setPrefs(pr.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="meals-page" className="space-y-4 sm:space-y-6">
      <MealsHowToTour />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <p className="text-neutral-500 text-xs sm:text-sm font-medium">Meal Intelligence</p>
          <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight mt-1">Kitchen</h1>
        </div>
      </div>

      <div className="flex gap-1 p-1 rounded-2xl overflow-x-auto" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`meals-tab-${t.id}`}
            className={`px-3 sm:px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap flex-shrink-0 ${tab === t.id ? 'bg-[#FF6B35]/15 text-[#FF6B35]' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'planner' && <PlannerTab api={api} plans={plans} prefs={prefs} onUpdate={fetchData} onBriefAction={triggerAction} />}
      {tab === 'kitchen' && <WhatsInKitchenTab api={api} prefs={prefs} />}
      {tab === 'profile' && <FoodProfileTab api={api} prefs={prefs} onUpdate={fetchData} />}
      {tab === 'eaters' && <EaterProfilesTab api={api} />}
      {tab === 'recipes' && <RecipesTab api={api} recipes={recipes} onUpdate={fetchData} />}
      {tab === 'ratings' && <RatingsTab api={api} />}
      {tab === 'dabba' && <DabbaTab api={api} />}
      {tab === 'ai-chef' && <AIChefTab api={api} prefs={prefs} onUpdate={fetchData} pendingAction={pendingAction} clearPendingAction={() => setPendingAction(null)} onGoToPlanner={() => setTab('planner')} />}

      {/* Floating Share with Kitchen Button */}
      <button
        data-testid="share-cook-view-btn"
        onClick={() => setShowCookQR(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm shadow-lg shadow-[#FF6B35]/20 transition-all hover:scale-105 active:scale-95"
        style={{ background: 'linear-gradient(135deg, #FF6B35, #E85D2A)', color: 'white' }}
      >
        <CookingPot size={20} weight="fill" />
        <span className="hidden sm:inline">Share with Cook</span>
        <ShareNetwork size={16} />
      </button>

      {showCookQR && <CookQRModal onClose={() => setShowCookQR(false)} />}
    </div>
  );
}
